import {IComponentController, IHttpResponse, IScope} from "angular";
import {UserDto} from "../../model/User";
import {UserEvent, UserEventType, UserService} from "../../../users_service/service/user.service";
import {ToastNotificationService} from "../../../utils/ToastNotificationService";
import {
    CustomerServiceName,
    ToastNotificationServiceName,
    UserServiceName,
    UserValidationServiceName
} from "../../../utils/names/serviceNames";
import './UsersGrid.scss';
import {EditUserDialogComponent} from "../../dialog/new_user/edit.user.dialog.component";
import {mapAccessTogglesGui} from "../../model/AccessLevelToggle";
import {getAbsoluteUrl} from "../../../utils/urlUtill";
import {UserValidationService} from "../../../users_service/service/user-validation.service";
import {UserView} from "../../model/UserFilter";
import {Customer} from "../../../customers/model/Customer";
import {TransferUsersDialogComponent} from "../../dialog/transfer_users/transfer-users.dialog.component";
import {DeleteUserDialogComponent} from "../../dialog/delete-user/delete.user.dialog.component";
import {CustomerService} from "../../../customer_service/service/CustomerService";
import {Principle} from "../../../brand_service/model/Principle";

interface UserGridScope extends IScope {
    loggedInUser: Principle;
    morph: ($event: MouseEvent, user: UserDto) => void;
    openMenu: ($mdMenu: angular.material.IMenuService, ev) => void;
    openEditCustomerDialog: ($event: MouseEvent, user: UserDto) => void;
    toggleSuperAdmin: (user: UserDto) => void;
    usersList: UserDto[]
    updateUserStatus: (status: boolean, user: UserDto) => void;
    showLoader: boolean;
    deleteUser: ($event, user: UserDto) => void;
    restoreUser: (user: UserDto) => void;
    isAbleToMorph: (user: UserDto) => boolean;
    sendWelcomeEmail: (user: UserDto) => void;
    sendResetPasswordEmail: (user: UserDto) => void;
}

export class UsersGrid implements IComponentController {

    public static instance = {
        template: require('./UsersGrid.html'),
        controller: [
            '$scope',
            UserServiceName,
            CustomerServiceName,
            ToastNotificationServiceName,
            '$mdDialog',
            UserValidationServiceName,
            UsersGrid],
        bindings: {
            users: '<',
            loggedInUser: '<'
        }
    };

    private users: UserDto[];
    private loggedInUser: Principle;

    constructor(private $scope: UserGridScope,
                private userService: UserService,
                private customerService: CustomerService,
                private toastNotificationService: ToastNotificationService,
                private $mdDialog: angular.material.IDialogService,
                private userValidationService: UserValidationService) {
        $scope.openEditCustomerDialog = this.openEditCustomerDialog;
        $scope.openMenu = this.openMenu;
        $scope.morph = this.morph;
        $scope.deleteUser = this.deleteUser;
        $scope.restoreUser = this.restoreUser;
        $scope.isAbleToMorph = this.isAbleToMorph;
        $scope.updateUserStatus = this.updateUserStatus;
        $scope.sendWelcomeEmail = this.sendWelcomeEmail;
        $scope.sendResetPasswordEmail = this.sendResetPasswordEmail;
        $scope.toggleSuperAdmin = this.toggleSuperAdmin;
        this.subscribeForNewUsers();
        this.subscribeForUserDeleted();
    }

    $onInit = (): void => {
        let $scope = this.$scope;
        $scope.showLoader = false;
        $scope.usersList = this.users;
        $scope.loggedInUser = this.loggedInUser;
    };

    $onChanges(onChangesObj: angular.IOnChangesObject): void {
        this.handleUsersChange(onChangesObj);
        this.handleLoggedInUserChange(onChangesObj);
    }

    private handleLoggedInUserChange(onChangesObj: angular.IOnChangesObject) {
        let loggedInUserChange = onChangesObj['loggedInUser'];
        if (loggedInUserChange && !loggedInUserChange.isFirstChange()) {
            this.$scope.loggedInUser = loggedInUserChange.currentValue;
        }
    }

    private handleUsersChange(onChangesObj: angular.IOnChangesObject) {
        let usersChange = onChangesObj['users'];
        if (usersChange && !usersChange.isFirstChange()) {
            this.$scope.usersList = usersChange.currentValue;
        }
    }

    isAbleToMorph = (user: UserDto): boolean => {
        return user.canBeMorphedInto;
    }

    openMenu = function ($mdMenu: angular.material.IMenuService, ev) {
        this.originatorEv = ev;
        $mdMenu.open(ev);
    };

    getMorphUrl = (user: UserDto) => getAbsoluteUrl('api/users/' + user.id + '/morph');

    morph = ($event: MouseEvent, user: UserDto) => {
        if (this.userValidationService.validateUser(user)) {
            window.location.href = this.getMorphUrl(user)
        }
    };

    subscribeForUserDeleted = () => {
        this.userService.subscribeForUserEventsWithType(UserEventType.DELETED, (userEvent) => {
            let userIndx = this.$scope.usersList.findIndex(u => u.id === userEvent.value.id);
            if (userIndx !== -1) {
                this.$scope.usersList.splice(userIndx, 1);
            }
        })
    };

    subscribeForNewUsers = () => {
        this.userService.subscribeForUserEventsWithType(UserEventType.CREATED, (userEvent: UserEvent) => {
            let userIndx = this.$scope.usersList.findIndex(u => u.id === userEvent.value.id);
            if (userIndx === -1) {
                this.$scope.usersList.push(userEvent.value);
            }
        })
    };

    openEditCustomerDialog = ($event: MouseEvent, user: UserDto) =>
        this.userService.getUserViewById(user.id, UserView.FULL_USER_VIEW)
            .then(resp => {
                if (resp.status === 200) {
                    let fullUser: UserDto = resp.data;
                    user.featureAccessLevel = mapAccessTogglesGui(fullUser.featureAccessLevel);
                    this.$mdDialog.show(EditUserDialogComponent.instance($event, fullUser, this.loggedInUser))
                        .then(updatedUser => {
                            let $scope = this.$scope;
                            let userIdx = $scope.usersList.findIndex(user => user.id === updatedUser.id);
                            $scope.usersList[userIdx] = updatedUser;
                        })
                }
            });

    restoreUser = (user: UserDto): void => {
        let $scope = this.$scope;
        $scope.showLoader = true;
        this.userService.restoreUser(user)
            .then((resp: IHttpResponse<void>) => {
                $scope.showLoader = false;
                if (resp.status === 200) {
                    user.isDeleted = false;
                    this.toastNotificationService.generateSuccessMessage("User successfully restored.")
                }
            }, (err) => {
                $scope.showLoader = false;
                this.toastNotificationService.generateErrorMessage("Failed to update user status.")
            });
    };

    deleteUser = ($event, user: UserDto): void => {
        let $scope = this.$scope;
        $scope.showLoader = true;
        this.customerService.findLoanOfficerCustomers(user)
            .then(
                resp => {
                    $scope.showLoader = false;
                    let customers = resp.data;
                    if (customers.length) {
                        this.showTransferUserDialog($event, user, customers);
                    } else {
                        this.showDeleteUserDialog($event, user);
                    }
                }, err => {
                    $scope.showLoader = false;
                    this.toastNotificationService.generateErrorMessage("Failed to fetch user customers!");
                }
            );
    };

    sendWelcomeEmail = (user: UserDto) => {
        this.userService.sendWelcomeEmail(user.id)
            .then(resp => {
                if (resp.status == 200) {
                    this.toastNotificationService.generateSuccessMessage("Welcoming email sent!")
                } else {
                    this.toastNotificationService.generateErrorMessage("Failed to send welcoming email");
                }
            })
    };

    sendResetPasswordEmail = (user: UserDto) => {
        this.userService.sendResetPasswordEmail(user.id)
            .then(resp => {
                if (resp.status == 200) {
                    this.toastNotificationService.generateSuccessMessage("Reset password email sent!")
                } else {
                    this.toastNotificationService.generateErrorMessage("Failed to send reset password email");
                }
            })
    };

    toggleSuperAdmin = (user: UserDto) => {
        if (this.isLoggedInUser(user)) {
            this.showConfirmSelfSuperAdminTransferDialog()
                .then(() => this.doToggleSuperAdmin(user))
        } else {
            this.doToggleSuperAdmin(user);
        }

    };


    private showConfirmSelfSuperAdminTransferDialog() {
        return this.$mdDialog.show(this.getConfirmSelfSuperAdminTransferDialog());
    }

    private getConfirmSelfSuperAdminTransferDialog() {
        return this.$mdDialog.confirm()
            .ok("Yes")
            .cancel("Cancel")
            .title("Are you sure you want to make yourself regular admin?")
            .textContent("You are a Super Admin. If you make yourself a regular admin, the super admin status will be able to be granted back only by other super admin.");
    }

    private doToggleSuperAdmin(user: UserDto) {
        this.userService.toggleSuperAdmin(user)
            .then(resp => {
                if (resp.status == 200) {
                    user.isSuperAdmin = !user.isSuperAdmin;
                    if (this.isLoggedInUser(user)) {
                        this.loggedInUser.isSuperAdmin = !this.loggedInUser.isSuperAdmin;
                    }
                }
            })
    }

    private isLoggedInUser(user: UserDto) {
        return this.loggedInUser.id === user.id;
    }

    private showTransferUserDialog = ($event, user: UserDto, userAssignedCustomers: Customer[]) => {
        this.$mdDialog.show(TransferUsersDialogComponent.instance($event, user, userAssignedCustomers))
            .then(
                resp => this.showDeleteUserDialog($event, user),
                err => err && this.toastNotificationService.generateErrorMessage("Failed to transfer user customers!")
            );
    };

    private showDeleteUserDialog = ($event: any,
                                    user: UserDto) =>
        this.$mdDialog.show(DeleteUserDialogComponent.instance($event, user))
            .then(
                resp => this.toastNotificationService.generateSuccessMessage("User has been successfully deleted!"),
                err => err && this.toastNotificationService.generateErrorMessage("Failed to delete user!")
            );

    updateUserStatus = (status: boolean, user: UserDto) => {
        let $scope = this.$scope;
        user.isActive = !status;
        $scope.showLoader = true;
        this.userService.updateUserStatus(user.id, user.isActive)
            .then(resp => {
                $scope.showLoader = false;
                this.toastNotificationService.generateSuccessMessage('User status has been successfully updated!')

            }, () => {
                $scope.showLoader = false;
                this.toastNotificationService.generateErrorMessage('Failed to update user status!')
            });
    };

}